import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, MessageSquare, Phone, User, UserRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, money, telLink, waLink } from "@/lib/api";

const quickLogJob = (jobId, type) => {
  api
    .post(`/jobs/${jobId}/interactions`, { type, outcome: "", next_action: "" })
    .then(() => toast.success(`${type} logged — add the response on the job's interaction log`))
    .catch(() => {});
};
import { PIPELINE } from "@/lib/rbac";
import { usePaged, ShowMore } from "@/lib/usePaged";

const COLUMN_TINT = {
  Lead: "bg-slate-100 text-slate-700",
  Quoted: "bg-violet-50 text-violet-700",
  Booked: "bg-blue-50 text-blue-700",
  "In Progress": "bg-amber-50 text-amber-700",
  Delivered: "bg-emerald-50 text-emerald-700",
  "Follow-up": "bg-crimson-50 text-crimson-700",
  "Repeat/Referral": "bg-fuchsia-50 text-fuchsia-700",
};

const STAGE_HINT = {
  Lead: "A new enquiry lands here — walk-in, call or DM.",
  Quoted: "You have sent a price. Waiting on the customer.",
  Booked: "Customer said yes and a slot is reserved.",
  "In Progress": "Car is in the bay, film going on.",
  Delivered: "Handed back. Warranty and invoice done.",
  "Follow-up": "Check in after a few weeks — gloss, care, review.",
  "Repeat/Referral": "They came back or sent someone. Your best revenue.",
};

export const JobsBoard = ({ jobs, canEdit, onMove, stages, showStudio, canDelete, onDelete, canDeleteJob = () => true }) => {
  const nav = useNavigate();
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const boardEmpty = jobs.length === 0;

  const drop = (status) => {
    setOverCol(null);
    if (!dragId) return;
    const job = jobs.find((j) => j.id === dragId);
    setDragId(null);
    if (job && job.status !== status) onMove(job, status);
  };

  return (
    <div className="flex flex-col gap-4 pb-4 lg:flex-row lg:overflow-x-auto" data-testid="kanban-board">
      {(stages && stages.length ? stages : PIPELINE).map((status, i) => (
        <BoardColumn
          key={status}
          status={status}
          index={i}
          boardEmpty={boardEmpty}
          jobs={jobs.filter((j) => j.status === status)}
          canEdit={canEdit}
          showStudio={showStudio}
          canDelete={canDelete}
          onDelete={onDelete}
          canDeleteJob={canDeleteJob}
          dragId={dragId}
          setDragId={setDragId}
          overCol={overCol}
          setOverCol={setOverCol}
          drop={drop}
          nav={nav}
        />
      ))}
    </div>
  );
};

const BoardColumn = ({
  status,
  index,
  boardEmpty,
  jobs,
  canEdit,
  showStudio,
  canDelete,
  onDelete,
  canDeleteJob,
  dragId,
  setDragId,
  overCol,
  setOverCol,
  drop,
  nav,
}) => {
  const paged = usePaged(jobs);
  const cards = paged.visible;
  return (
          <div
            key={status}
            data-testid={`kanban-column-${status}`}
            className="w-full shrink-0 rounded-md border border-brand-line bg-white p-3 lg:w-[264px]"
          >
            <div className="mb-3 flex items-center justify-between">
              <span
                className={`rounded px-2 py-0.5 text-xs font-semibold ${
                  COLUMN_TINT[status] || "bg-slate-100 text-slate-700"
                }`}
              >
                {boardEmpty ? `${index + 1}. ${status}` : status}
              </span>
              <span
                data-testid={`kanban-count-${status}`}
                className="text-xs font-semibold text-slate-500"
              >
                {jobs.length}
              </span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
              {cards.length === 0 &&
                (boardEmpty ? (
                  <p
                    data-testid={`kanban-stage-hint-${status}`}
                    className="w-full shrink-0 rounded-md border border-dashed border-brand-line bg-slate-50/60 p-3 text-xs leading-relaxed text-slate-500"
                  >
                    {STAGE_HINT[status] || "Jobs at this stage show up here."}
                  </p>
                ) : (
                  <p className="w-full shrink-0 py-6 text-center text-xs text-slate-400">No jobs at this stage.</p>
                ))}
              {cards.map((j) => (
                <div
                  key={j.id}
                  data-testid={`kanban-card-${j.id}`}
                  onClick={() => nav(`/jobs/${j.id}`)}
                  className={`w-60 shrink-0 cursor-pointer rounded-md border bg-white p-3 transition-shadow duration-200 hover:shadow-sm lg:w-full ${
                    j.warranty_state === "incomplete"
                      ? "border-rose-400 ring-1 ring-rose-200"
                      : j.warranty_state === "pending"
                      ? "border-orange-400 ring-1 ring-orange-200"
                      : j.warranty_state === "complete"
                      ? "border-emerald-400 ring-1 ring-emerald-200"
                      : "border-brand-line"
                  }`}
                >
                  <div className="flex items-start justify-between gap-1.5">
                    <p className="truncate text-sm font-semibold text-brand-ink">{j.customer_name}</p>
                    {canDelete && canDeleteJob(j) && (
                      <button
                        type="button"
                        data-testid={`kanban-delete-${j.id}`}
                        onClick={(e) => { e.stopPropagation(); onDelete(j); }}
                        className="shrink-0 rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600"
                        title="Delete job"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {j.warranty_state === "incomplete" && (
                    <p
                      data-testid={`kanban-card-material-pending-${j.id}`}
                      className="mt-1.5 rounded bg-rose-50 px-2 py-1 text-[10px] font-semibold text-rose-700"
                    >
                      Material log incomplete
                    </p>
                  )}
                  {j.warranty_state === "pending" && (
                    <p
                      data-testid={`kanban-card-warranty-pending-${j.id}`}
                      className="mt-1.5 rounded bg-orange-50 px-2 py-1 text-[10px] font-semibold text-orange-700"
                    >
                      Warranty registration pending
                    </p>
                  )}
                  {j.warranty_state === "complete" && (
                    <p
                      data-testid={`kanban-card-warranty-complete-${j.id}`}
                      className="mt-1.5 rounded bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700"
                    >
                      Warranty registered
                    </p>
                  )}
                  {showStudio && j.studio_name && (
                    <span
                      data-testid={`kanban-card-studio-${j.id}`}
                      className="mt-1 inline-block rounded-full bg-crimson-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-crimson-700"
                    >
                      {j.studio_name}
                    </span>
                  )}
                  {j.cs_person_name && (
                    <p
                      data-testid={`kanban-card-cs-${j.id}`}
                      className="mt-1 flex items-center gap-1.5 truncate text-xs font-medium text-violet-700"
                    >
                      <UserRound className="h-3 w-3" strokeWidth={2} />
                      Sales: {j.cs_person_name}
                    </p>
                  )}
                  <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-slate-500">
                    <Car className="h-3 w-3" strokeWidth={1.8} />
                    {j.vehicle ? `${j.vehicle.make} ${j.vehicle.model}` : "—"}
                  </p>
                  <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-slate-500">
                    <User className="h-3 w-3" strokeWidth={1.8} />
                    {j.installer_name || "Unassigned"}
                  </p>
                  <p className="mt-2 text-xs font-semibold text-slate-700">{money(j.price)}</p>
                  {j.customer_phone && (
                    <div className="mt-2 flex gap-2 border-t border-brand-line pt-2">
                      <button
                        type="button"
                        data-testid={`kanban-call-${j.id}`}
                        onClick={(e) => { e.stopPropagation(); quickLogJob(j.id, "Call"); window.location.href = telLink(j.customer_phone); }}
                        className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-brand-line py-1.5 text-xs font-medium text-brand-ink hover:bg-brand-tint"
                      >
                        <Phone className="h-3.5 w-3.5" /> Call
                      </button>
                      <button
                        type="button"
                        data-testid={`kanban-whatsapp-${j.id}`}
                        onClick={(e) => { e.stopPropagation(); quickLogJob(j.id, "WhatsApp"); window.open(waLink(j.customer_phone), "_blank"); }}
                        className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-emerald-200 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                      >
                        <MessageSquare className="h-3.5 w-3.5" /> WhatsApp
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <ShowMore paged={paged} testid={`kanban-show-more-${status}`} />
          </div>
  );
};
